import { useEffect, useState } from 'react';

import clsx from 'clsx';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetStringMapsQuery } from '@/app/services/lookups';

import {
  PARTNERSHIP_UI_TABS_ATTRIBUTE_NAME,
  PARTNERSHIP_UI_TABS_OBJECT_TYPE_CODE,
  PartnershipBillingType,
  PartnershipTypeValue,
} from './constants';
import { PartnershipFormForm } from './PartnershipFormForm';

type PartnershipType = {
  attributeValue: number;
  value: string;
};

type PartnershipFormTabsProps = {
  className?: string;
  types: PartnershipType[];
  viewOnly?: boolean;
};

export const PartnershipFormTabs = ({
  className,
  types,
  viewOnly = false,
}: PartnershipFormTabsProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [type, setType] = useState<number | undefined>(
    types[0]?.attributeValue,
  );

  const dispatch = useAppDispatch();
  const { partnership } = useAppSelector((state) => state.partnerships);
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const currentPartnershipItem = partnership?.[0];

  const { data: partnershipTypes } = useGetStringMapsQuery(
    {
      objectTypeCode: PARTNERSHIP_UI_TABS_OBJECT_TYPE_CODE,
      attributeName: PARTNERSHIP_UI_TABS_ATTRIBUTE_NAME,
    },
    {
      skip: types.length === 0,
    },
  );

  useEffect(() => {
    const isSpecialBillingTab =
      (types ?? partnershipTypes)?.[activeIndex]?.value === 'Special Billing';

    if (
      currentPartnershipItem?.percent1 === 100 ||
      (!currentPartnershipItem?.account1 && workingDonorSchedule?.owner)
    ) {
      const billAccountTabIndex = types.findIndex(
        (t) => t.value === 'Bill Account',
      );
      if (billAccountTabIndex !== -1 && !isSpecialBillingTab) {
        setActiveIndex(billAccountTabIndex);
        setType(types[billAccountTabIndex].attributeValue);
      }
    }
  }, [
    currentPartnershipItem?.percent1,
    currentPartnershipItem?.account1,
    workingDonorSchedule?.owner,
    workingDonorSchedule?.donor,
    types,
    partnershipTypes,
    activeIndex,
    dispatch,
  ]);

  const handleTabChange = (e: TabViewTabChangeEvent) => {
    setActiveIndex(e.index);
    const newType = (types ?? partnershipTypes)?.[e.index]?.attributeValue;
    setType(newType as number);
  };

  const tabViewClassName = clsx(
    'w-full [&_.p-tabview-panels]:pt-4',
    'tab-view-shading',
  );

  const partnershipBillingType =
    currentPartnershipItem?.partnershipBillingType ?? null;

  useEffect(() => {
    if (partnershipBillingType !== PartnershipBillingType.SPECIAL_PARTNERSHIP)
      return;

    const specialBillingIndex = types.findIndex(
      (t) => t.attributeValue === PartnershipTypeValue.SPECIAL_BILLING,
    );
    if (specialBillingIndex !== -1) {
      setActiveIndex(specialBillingIndex);
      setType(types[specialBillingIndex].attributeValue);
    }
  }, [partnershipBillingType, types]);

  const isTabDisabled = (attributeValue: number): boolean => {
    if (partnershipBillingType === PartnershipBillingType.DONOR_PARTNERSHIP) {
      return attributeValue === PartnershipTypeValue.SPECIAL_BILLING;
    }
    if (partnershipBillingType === PartnershipBillingType.SPECIAL_PARTNERSHIP) {
      return attributeValue === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING;
    }
    return false;
  };

  return (
    <div className={`w-full ${className}`.trim()}>
      <TabView
        className={tabViewClassName}
        activeIndex={activeIndex}
        onTabChange={handleTabChange}
      >
        {(types ?? partnershipTypes).map((partnershipType) => (
          <TabPanel
            key={partnershipType.attributeValue}
            header={partnershipType.value}
            disabled={isTabDisabled(partnershipType.attributeValue)}
          >
            <PartnershipFormForm
              type={type as number}
              viewOnly={viewOnly}
              showPercentages={activeIndex !== 0}
              showBillingSplit={activeIndex !== 0}
            />
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
};
